import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, ArrowLeft, Calculator, FileSignature, 
  ShieldCheck, GraduationCap, Users, HeartPulse, FileText, AlertTriangle, CheckCircle2,
  Scale, Gavel, BookOpen, Briefcase, CalendarCheck, MessageSquareMore
} from 'lucide-react';

// --- IMÁGENES DEL CARRUSEL (Sección Dolor) ---
const SI_CAROUSEL_IMAGES = [
  "https://zaphy.com.co/wp-content/uploads/2026/01/innovacion12.jpeg",
  "https://zaphy.com.co/wp-content/uploads/2026/01/servicios-indep.jpg",
  "https://zaphy.com.co/wp-content/uploads/2026/01/home-3.jpg"
];

const ServiciosIndependientes = ({ language }) => { 
  
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % SI_CAROUSEL_IMAGES.length);
    }, 4000); 
    return () => clearInterval(interval);
  }, []);
  
  // --- DICCIONARIO DE TEXTOS CON ENFOQUE HORMOZI Y MÁXIMA DENSIDAD SEMÁNTICA SEO ---
  const t = {
    es: {
      back: "Volver al Inicio",
      heroTag: "Afiliaciones y Asesoría Laboral Puntual en Colombia",
      heroTitle: "Soluciones de Empleo Ágiles",
      heroSubtitle: "Sin Contratos ni Ataduras",
      heroDesc: "¿Necesitas resolver un requerimiento específico sin comprometer tu flujo de caja mensual? Ejecutamos afiliaciones a seguridad social, diseño de contratos laborales y defensa legal por proyectos. Pagas estrictamente por lo que usas.",
      heroBtnQuote: "Cotizar Servicio",
      heroBtnWhatsApp: "Escríbenos por WhatsApp",
      imgBadgeTitle: "Flexibilidad",
      imgBadgeDesc: "Pago por Uso Activo",
      painTag: "¿Te ha pasado?",
      painTitle: "No necesitas un departamento entero, necesitas resolver un problema hoy",
      painDesc: "A veces solo requieres afiliar a un colaborador nuevo a la seguridad social de forma express, redactar un contrato comercial blindado o contestar una tutela o demanda laboral específica. No tiene sentido financiero amarrarte a un contrato de retención mensual si tu necesidad operativa es de un solo día.",
      painHighlight: "Adaptamos la ingeniería legal de Zaphy a la velocidad y presupuesto de tu negocio independiente o PyME, sin cláusulas de permanencia.",
      painBadgeTitle: "Ventaja",
      painBadgeDesc: "Cero Costos Fijos",
      
      // Banner Estratégico de Mitigación Económica (Modelo Hormozi de Reversión de Riesgo)
      bannerTitle: "Soluciones de Gestión Humana y SST Bajo Demanda",
      bannerDesc: "Ponemos a tu disposición la misma infraestructura técnica y el respaldo de abogados laboralistas e ingenieros de SST que disfrutan las grandes corporaciones, pero fraccionado en servicios individuales. Solucionas el pendiente legal de tu empresa, proteges tu patrimonio y liberas flujo de caja.",
      
      gridTitle: "Menú de Soluciones Independientes",
      gridSubtitle: "Selecciona con total autonomía la solución exacta que tu actividad independiente o empresa requiere resolver hoy.",
      services: [
        { title: "Afiliaciones a Seguridad Social", desc: "Vinculación legal express y radicación de novedades ante EPS, ARL, Fondos de Pensión (AFP) y Cajas de Compensación Familiar para independientes y PyMEs." },
        { title: "Contratos de Trabajo a la Medida", desc: "Diseño y redacción de contratos laborales individuales, prestación de servicios comerciales y cláusulas de confidencialidad con blindaje jurídico completo." },
        { title: "Diseño y Actualización de RIT", desc: "Elaboración e ingeniería del Reglamento Interno de Trabajo adaptado a las últimas reformas obligatorias vigentes del Ministerio de Trabajo." },
        { title: "Asesoría de Estándares Mínimos", desc: "Autoevaluación diagnóstica basada en la Resolución 0312, diseño de matrices de peligros (GTC 45) y estructuración del Plan de Trabajo Anual de SST." },
        { title: "Planes de Emergencia & Comités", desc: "Documentación técnica de planes de contingencia, conformación digital de brigadas de emergencia, COPASST y actas del Comité de Convivencia." },
        { title: "Capacitaciones de Ley Certificadas", desc: "Ejecución presencial o virtual de módulos obligatorios: Inducción SST, manejo de EPP, prevención de acoso laboral y hábitos de vida saludables." },
        { title: "Auditorías de Cumplimiento", desc: "Revisiones documentales internas e inspecciones técnicas de campo para preparar tu operación antes de visitas sorpresivas de entes de control." }
      ],
      legalTag: "Blindaje Jurídico Laboral",
      legalTitle: "Tu Defensoría Legal Externa por Proyectos",
      legalDesc: "Protege tu empresa ante demandas, derechos de petición y requerimientos administrativos. Nuestro equipo de abogados laboralistas asume la defensa técnica de tu PyME mediante servicios puntuales sin cobros mensuales recurrentes.",
      legalCards: [
        { title: "Contratos Blindados", desc: "Revisión técnica de contratos de trabajo vigentes para detectar vacíos legales o riesgos de demandas laborales futuras." },
        { title: "Defensa & Conciliación", desc: "Representación presencial en audiencias de conciliación ante el Ministerio de Trabajo y contestación de demandas laborales." },
        { title: "Conceptos Jurídicos", desc: "Dictámenes legales rápidos sobre fueros de estabilidad, despidos con justa causa, indemnizaciones y cálculos de liquidación." },
        { title: "Derechos de Petición", desc: "Estructuración y respuesta legal a derechos de petición de ex-empleados, requerimientos de la ARL o fiscalizaciones de entidades públicas." }
      ],
      seoTitle: 'Preguntas Frecuentes sobre Servicios Laborales Independientes',
      seoSubtitle: 'Resolvemos tus dudas sobre contratos por días, afiliaciones de seguridad social y asesorías puntuales con Zaphy.',
      faqs: [
        { q: '¿Es obligatorio firmar un contrato de permanencia mensual con Zaphy para acceder a los servicios?', a: '¡Para nada! Esa es nuestra mayor fortaleza en este plan. Si solo necesitas redactar un Reglamento Interno de Trabajo, afiliar a un empleado doméstico o responder un derecho de petición, nos contratas únicamente para ese proyecto. Pagas por el servicio ejecutado y tu relación contractual termina al entregarte los soportes.' },
        { q: '¿Qué validez legal tienen los contratos y documentos diseñados por Zaphy?', a: 'Absoluta. Cada contrato de trabajo, RIT o documento técnico de SST es elaborado milimétricamente por abogados laboralistas e ingenieros con licencias vigentes en Colombia. Nos aseguramos de que cumplan de forma estricta con la jurisprudencia más reciente del Código Sustantivo del Trabajo.' },
        { q: '¿Soy trabajador independiente, Zaphy me puede ayudar con mis afiliaciones a seguridad social?', a: 'Sí, estructuramos todo tu proceso de vinculación como independiente ante EPS, AFP y ARL (en los niveles de riesgo correspondientes a tu actividad). Te evitamos trámites engorrosos y filas para que operes con total tranquilidad legal.' },
        { q: '¿Puedo contratar una capacitación de SST o un simulacro de emergencia de forma suelta?', a: 'Claro que sí. Si tu empresa ya tiene el SG-SST diseñado pero te falta ejecutar el cronograma de capacitaciones de la Resolución 0312 o el simulacro anual, enviamos a nuestros especialistas para dictar el módulo específico y te entregamos las planillas de asistencia firmadas como evidencia legal válida.' },
        { q: '¿Qué pasa si después de un servicio puntual quiero contratar el outsourcing mensual de nómina o RR.HH.?', a: 'Es el paso natural de muchas PyMEs en crecimiento. Puedes iniciar solucionando tus urgencias documentales a la carta y, a medida que tu flujo de caja se estabilice, migrar sin complicaciones a nuestro ecosistema de gestión integral mensual de nómina electrónica, SST o Recursos Humanos para delegar el área por completo.' }
      ],
      ctaTitle: "¿Listo para resolver ese pendiente legal o administrativo hoy mismo?",
      ctaDesc: "Contrata estrictamente lo que necesitas para tu PyME o actividad independiente. Cuéntanos tu caso y recibe una propuesta comercial ágil en minutos.",
      ctaBtnQuote: "Iniciar Cotización",
      ctaBtnSchedule: "Agendar Asesoría",
      ctaBtnWhatsApp: "Hablemos por WhatsApp"
    },
    en: {
      back: "Back to Home",
      heroTag: "Social Security Affiliations and One-off Labor Advisory in Colombia",
      heroTitle: "Agile Employment Solutions",
      heroSubtitle: "No Retainers, No Strings Attached",
      heroDesc: "Need to resolve a specific requirement without compromising your monthly cash flow? We execute social security affiliations, labor contract drafting, and project-based legal defense. Pay strictly for what you use.",
      heroBtnQuote: "Start Quote",
      heroBtnWhatsApp: "Chat on WhatsApp",
      imgBadgeTitle: "Flexibility",
      imgBadgeDesc: "Pay Per Active Use",
      painTag: "Has this happened?",
      painTitle: "You don't need a whole department, you need to solve a problem today",
      painDesc: "Sometimes you just need to affiliate a new collaborator express, draft a shielded commercial contract, or answer a specific lawsuit. It makes no financial sense to tie yourself to a monthly retainer if your operational need is a single day.",
      painHighlight: "We adapt Zaphys legal engineering to the speed and budget of your freelance activity or PyME, without permanent clauses.",
      painBadgeTitle: "Advantage",
      painBadgeDesc: "Zero Fixed Costs",
      
      bannerTitle: "On-Demand Human Management & OHS Solutions",
      bannerDesc: "We provide you with the same technical infrastructure and backing of labor lawyers and OHS engineers that large corporations enjoy, but fractioned into single services. You solve your business pending legal issues, protect your assets, and release cash flow.",
      
      gridTitle: "Menu of Independent Solutions",
      gridSubtitle: "Select with total autonomy the exact solution that your freelance activity or business needs to solve today.",
      services: [
        { title: "Social Security Affiliations", desc: "Express linking and novelty filing before EPS, ARL, Pension Funds (AFP), and Family Compensation Funds for freelancers and PyMEs." },
        { title: "Custom Labor Contracts", desc: "Design and drafting of individual labor contracts, commercial service agreements, and confidentiality clauses with full legal shielding." },
        { title: "RIT Design & Update", desc: "Drafting and engineering of the Internal Labor Regulations adapted to the latest mandatory reforms from the Ministry of Labor." },
        { title: "Minimum Standards Advisory", desc: "Diagnostic self-assessment based on Resolution 0312, hazard matrix design (GTC 45), and OHS Annual Work Plan structuring." },
        { title: "Emergency Plans & Committees", desc: "Technical documentation of contingency plans, digital formation of emergency brigades, COPASST, and Coexistence Committee minutes." },
        { title: "Certified OHS Training", desc: "In-person or virtual execution of mandatory modules: OHS Induction, PPE handling, workplace harassment prevention, and healthy habits." },
        { title: "Compliance Audits", desc: "Internal document reviews and technical field inspections to prepare your operation before surprise visits from control entities." }
      ],
      legalTag: "Labor Legal Shielding",
      legalTitle: "Your External Legal Defense by Projects",
      legalDesc: "Protect your company against lawsuits, petitions, and administrative requirements. Our team of labor lawyers assumes your PyMEs technical defense through specific services without recurring monthly fees.",
      legalCards: [
        { title: "Shielded Contracts", desc: "Technical review of current employment contracts to detect legal loopholes or future labor lawsuit risks." },
        { title: "Defense & Conciliation", desc: "In-person representation in conciliation hearings before the Ministry of Labor and answering labor lawsuits." },
        { title: "Legal Concepts", desc: "Fast legal rulings regarding stability jurisdictions, terminations with just cause, severances, and settlement calculations." },
        { title: "Petitions and Responses", desc: "Legal structuring and response to former employee petitions, ARL requirements, or public entity audits." }
      ],
      seoTitle: 'Frequently Asked Questions about Independent Labor Services',
      seoSubtitle: 'We solve your doubts about daily contracts, social security affiliations, and precise advisory sessions with Zaphy.',
      faqs: [
        { q: 'Is it mandatory to sign a long-term contract with Zaphy to access services?', a: 'Not at all! That is our biggest strength in this plan. If you only need to draft an Internal Labor Regulation, register a domestic employee, or respond to a petition, you hire us only for that project. You pay for the executed service, and our relationship concludes upon delivery.' },
        { q: 'What legal validity do contracts and documents designed by Zaphy hold?', a: 'Absolute. Every employment contract, RIT, or OHS technical document is carefully crafted by labor lawyers and licensed engineers in Colombia. We ensure they strictly comply with the latest jurisprudence of the Substantive Labor Code.' },
        { q: 'I am a freelancer, can Zaphy help me with my social security registrations?', a: 'Yes, we structure your entire linking process as a freelancer before EPS, AFP, and ARL (at the risk levels corresponding to your activity). We avoid tedious paperwork and queues for your legal peace of mind.' },
        { q: 'Can I hire an OHS training session or an emergency drill independently?', a: 'Of course. If your company already has the SG-SST designed but you lack execution on the Resolution 0312 training schedule or the annual drill, we send our specialists to dictate the specific module and hand over valid logs.' },
        { q: 'What happens if after a one-off service I want to hire the monthly payroll or HR outsourcing?', a: 'It is the natural step for many growing PyMEs. You can start by solving your document emergencies a la carte and, as your cash flow stabilizes, seamlessly migrate to our comprehensive monthly ecosystem.' }
      ],
      ctaTitle: "Ready to solve that pending legal or administrative issue today?",
      ctaDesc: "Hire strictly what you need for your PyME or freelance activity. Tell us your case and receive a prompt commercial quote in minutes.",
      ctaBtnQuote: "Start Quote",
      ctaBtnSchedule: "Schedule Advisory",
      ctaBtnWhatsApp: "Chat via WhatsApp"
    }
  };

  const text = t[language] || t.es;

  const handleWhatsAppRedirect = () => {
    const targetUrl = `https://api.whatsapp.com/send?phone=573114044203&text=${encodeURIComponent("¡Hola, Zaphy! 🚀 Estuve revisando el portafolio de Servicios Independientes en su web. Me interesa cotizar una solución puntual para mi negocio sin ataduras de contratos mensuales.")}`;
    window.open(targetUrl, '_blank');
  };

  return (
    <div className="animate-[fadeIn_0.5s_ease-out] pt-32 pb-20 min-h-screen bg-gray-50 font-[Poppins]">
      
      {/* --- 1. HERO SECTION (CON INTERFAZ DE DOBLE ACCIÓN ASOCIADA AL COLOR DEL COMPONENTE) --- */}
      <div className="relative bg-[#0F172A] text-white py-24 overflow-hidden mb-16">
         <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=80)` }}></div>
         <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-[#6366F1]/80"></div>
         
         <div className="max-w-7xl mx-auto px-4 relative z-10">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors font-medium">
               <ArrowLeft size={20} /> {text.back}
            </button>
            
            <div className="flex flex-col md:flex-row gap-12 items-center">
                <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-xl bg-[#6366F1]/20 text-[#818cf8] border border-[#6366F1]/30">
                            <Settings size={40} />
                        </div>
                        <span className="text-[#818cf8] font-bold tracking-widest uppercase text-xs md:text-sm">{text.heroTag}</span>
                    </div>
                    
                    <h1 className="text-3xl md:text-5xl font-extrabold mb-6 leading-tight">
                    {text.heroTitle} <br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6366F1] to-[#a5b4fc]">{text.heroSubtitle}</span>
                    </h1>
                    
                    <p className="text-xl text-gray-300 max-w-2xl leading-relaxed mb-8">
                    {text.heroDesc}
                    </p>

                    {/* INTERFAZ DE DOBLE BOTÓN COMPLEMENTARIO (SÓLIDO E INVERTIDO) */}
                    <div className="flex flex-wrap gap-4">
                        <button 
                          onClick={() => navigate('/cotizador')} 
                          className="bg-[#6366F1] text-white font-bold py-4 px-8 rounded-2xl shadow-lg shadow-[#6366F1]/40 transition-all flex items-center gap-2 transform hover:-translate-y-1 hover:bg-[#4f46e5] text-xs md:text-sm uppercase tracking-wide"
                        >
                            <Calculator size={20}/> {text.heroBtnQuote}
                        </button>

                        <button 
                          onClick={handleWhatsAppRedirect} 
                          className="bg-transparent border-2 border-[#6366F1] text-[#818cf8] font-bold py-4 px-8 rounded-2xl transition-all flex items-center gap-2 transform hover:-translate-y-1 hover:bg-[#6366F1] hover:text-white shadow-lg shadow-indigo-500/5 text-xs md:text-sm uppercase tracking-wide"
                        >
                            <MessageSquareMore size={20}/> {text.heroBtnWhatsApp}
                        </button>
                    </div>
                </div>

                {/* IMAGEN HERO */}
                <div className="w-full max-w-md mt-10 md:mt-0"> 
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-[#6366F1] to-[#4f46e5] rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-[#0F172A]">
                             <img 
                                src="https://zaphy.com.co/wp-content/uploads/2026/01/independientes.png" 
                                alt="Servicios Laborales Independientes y de Pago por Uso | Zaphy" 
                                className="w-full h-auto object-cover transform transition-transform duration-500 group-hover:scale-105"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-transparent to-transparent opacity-60"></div>
                            <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-md border border-white/20 p-3 rounded-xl flex items-center gap-3">
                                <div className="bg-[#6366F1] p-1.5 rounded-full text-white"><CheckCircle2 size={16} /></div>
                                <div>
                                    <p className="text-xs text-[#c7d2fe] uppercase font-bold">{text.imgBadgeTitle}</p>
                                    <p className="text-sm font-bold text-white">{text.imgBadgeDesc}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
         </div>
      </div>

      {/* --- 2. EL PROBLEMA --- */}
      <div className="max-w-7xl mx-auto px-4 mb-16">
          <div className="bg-white rounded-[40px] p-8 md:p-12 shadow-xl border border-gray-100 flex flex-col md:flex-row items-center gap-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-bl-[100px] z-0"></div>
              
              <div className="md:w-1/2 relative z-10">
                  <div className="inline-flex items-center gap-2 bg-yellow-50 text-yellow-600 px-4 py-2 rounded-full font-bold text-sm mb-4 border border-yellow-100">
                      <AlertTriangle size={16} /> {text.painTag}
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{text.painTitle}</h2>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">{text.painDesc}</p>
                  <p className="font-bold text-gray-800 border-l-4 border-[#6366F1] pl-4">{text.painHighlight}</p>
              </div>

              {/* Columna Derecha: Carrusel de Imágenes */}
              <div className="w-full md:w-1/2 relative z-10 mt-12 md:mt-0">
                  <div className="absolute -inset-4 bg-indigo-100 rounded-[40px] transform -rotate-2 z-0"></div>
                  
                  <div className="relative z-10 rounded-[30px] shadow-lg w-full h-64 md:h-80 overflow-hidden bg-gray-200">
                      {SI_CAROUSEL_IMAGES.map((img, idx) => (
                          <img 
                            key={idx}
                            src={img} 
                            alt={`Soluciones Flexibles e Independientes de RRHH ${idx + 1}`} 
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${idx === currentImage ? 'opacity-100' : 'opacity-0'}`}
                            onError={(e) => { e.target.src = "https://via.placeholder.com/800x600/6366F1/FFFFFF?text=Servicios+Zaphy"; }}
                          />
                      ))}
                      
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
                          {SI_CAROUSEL_IMAGES.map((_, idx) => (
                              <div 
                                key={idx} 
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentImage ? 'bg-[#6366F1] w-4' : 'bg-white/50'}`}
                              />
                          ))}
                      </div>
                  </div>

                  <div className="absolute -bottom-6 -left-6 z-20 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded-full text-green-600"><CheckCircle2 size={24}/></div>
                      <div><p className="text-xs text-gray-500 font-bold uppercase">{text.painBadgeTitle}</p><p className="text-sm font-bold text-gray-900">{text.painBadgeDesc}</p></div>
                  </div>
              </div>
          </div>
      </div>

      {/* --- BANNER ESTRATÉGICO DE REVERSIÓN DE RIESGO COMERCIAL (ENFOQUE HORMOZI) --- */}
      <div className="max-w-7xl mx-auto px-4 mb-24">
         <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-[30px] p-8 md:p-10 border border-slate-700/50 shadow-xl flex flex-col md:flex-row items-center gap-8">
            <div className="bg-[#6366F1]/20 p-4 rounded-2xl text-[#818cf8] shrink-0 border border-[#6366F1]/30">
               <ShieldCheck size={40} />
            </div>
            <div>
               <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{text.bannerTitle}</h3>
               <p className="text-slate-300 text-sm md:text-base leading-relaxed">{text.bannerDesc}</p>
            </div>
         </div>
      </div>

      {/* --- 3. GRID DE SERVICIOS (MENÚ FLEXIBLE A LA CARTA) --- */}
      <div className="max-w-7xl mx-auto px-4 mb-24">
         <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">{text.gridTitle}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{text.gridSubtitle}</p>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {text.services.map((srv, idx) => {
               const icons = [Users, FileSignature, FileText, AlertTriangle, GraduationCap, HeartPulse, ShieldCheck];
               const Icon = icons[idx] || Settings;
               const configs = [
                 { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'hover:border-indigo-500' },
                 { bg: 'bg-blue-50', text: 'text-blue-600', border: 'hover:border-blue-500' },
                 { bg: 'bg-orange-50', text: 'text-orange-600', border: 'hover:border-orange-400' },
                 { bg: 'bg-red-50', text: 'text-red-600', border: 'hover:border-red-500' },
                 { bg: 'bg-purple-50', text: 'text-purple-600', border: 'hover:border-purple-500' },
                 { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'hover:border-emerald-500' },
                 { bg: 'bg-sky-50', text: 'text-sky-600', border: 'hover:border-sky-500' }
               ];
               const config = configs[idx] || configs[0];

               return (
                  <div key={idx} className={`bg-white p-8 rounded-3xl border border-gray-100 hover:shadow-xl transition-all group ${config.border}`}>
                      <div className={`w-14 h-14 ${config.bg} ${config.text} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                         <Icon size={28} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{srv.title}</h3>
                      <p className="text-gray-500 text-xs md:text-sm leading-relaxed">{srv.desc}</p>
                  </div>
               );
            })}
         </div>
      </div>

      {/* --- 4. SECCIÓN JURÍDICA --- */}
      <div className="relative py-24 bg-[#8B5CF6] text-white overflow-hidden mb-24 rounded-[40px] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/legal-paper.png')]"></div>
          
          <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-16">
                  
                  <div className="lg:w-1/2">
                      <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full font-bold text-sm mb-6 text-purple-100">
                          <Scale size={16} /> {text.legalTag}
                      </div>
                      <h2 className="text-4xl md:text-5xl font-extrabold mb-6">{text.legalTitle}</h2>
                      <p className="text-lg text-purple-100 mb-8 leading-relaxed">{text.legalDesc}</p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {text.legalCards.map((card, idx) => {
                             const icons = [FileSignature, Gavel, BookOpen, Briefcase];
                             const Icon = icons[idx];
                             return (
                               <div key={idx} className="bg-white/10 p-4 rounded-xl border border-white/10 hover:bg-white/20 transition-colors">
                                  <Icon size={32} className="text-purple-300 mb-3"/>
                                  <h4 className="font-bold text-lg mb-1">{card.title}</h4>
                                  <p className="text-xs text-purple-100/80">{card.desc}</p>
                               </div>
                             );
                          })}
                      </div>
                  </div>

                  <div className="lg:w-1/2 relative">
                      <div className="absolute -inset-4 bg-gradient-to-tr from-purple-400 to-indigo-600 rounded-full blur-3xl opacity-40 animate-pulse"></div>
                      <div className="relative bg-white/5 backdrop-blur-md border border-white/10 p-2 rounded-3xl shadow-2xl">
                          <img 
                            src="https://zaphy.com.co/wp-content/uploads/2026/01/WhatsApp-Image-2026-01-15-at-9.23.13-PM.jpeg" 
                            alt="Respaldo Legal de Abogados Laboralistas | Zaphy" 
                            className="rounded-2xl w-full"
                          />
                      </div>
                  </div>

              </div>
          </div>
      </div>

      {/* --- 4.5: BLOQUE DE PREGUNTAS FRECUENTES (SEO BOOST SEMÁNTICO) --- */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
         <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">{text.seoTitle}</h2>
            <p className="text-gray-500 text-xs md:text-sm">{text.seoSubtitle}</p>
         </div>
         <div className="space-y-4">
            {text.faqs.map((faq, idx) => (
               <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-slate-200 transition-all">
                  <h3 className="text-sm md:text-base font-bold text-gray-950 mb-2 flex items-start gap-2">
                     <span className="text-indigo-600 shrink-0">❓</span> {faq.q}
                  </h3>
                  <p className="text-gray-600 text-xs md:text-sm leading-relaxed pl-6">{faq.a}</p>
               </div>
            ))}
         </div>
      </div>

      {/* --- 5. CTA FINAL --- */}
      <div className="max-w-4xl mx-auto px-4 text-center">
         <div className="bg-gradient-to-br from-[#0F172A] to-[#6366F1] rounded-[40px] p-12 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl -mr-16 -mt-16"></div>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-6 relative z-10">{text.ctaTitle}</h2>
            <p className="text-indigo-100 text-base md:text-lg mb-8 max-w-2xl mx-auto relative z-10">{text.ctaDesc}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
                <button 
                  onClick={() => navigate('/cotizador')} 
                  className="bg-white text-indigo-900 font-bold py-4 px-8 rounded-xl shadow-lg hover:bg-indigo-50 transition-all transform hover:-translate-y-1 relative z-10 flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <Calculator size={20}/>
                    {text.ctaBtnQuote}
                </button>

                <button 
                    onClick={handleWhatsAppRedirect}
                    className="bg-emerald-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:bg-emerald-700 transition-all transform hover:-translate-y-1 relative z-10 flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <MessageSquareMore size={20}/>
                    {text.ctaBtnWhatsApp}
                </button>

                <a 
                  href="https://calendar.app.google/UYZ2ERubo552yRg19" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="bg-transparent border-2 border-white text-white font-bold py-4 px-8 rounded-xl hover:bg-white/10 transition-all relative z-10 flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <CalendarCheck size={20}/>
                    {text.ctaBtnSchedule}
                </a>
            </div>
         </div>
      </div>

    </div>
  );
};

export default ServiciosIndependientes;